import React, { useState, useCallback, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  TextField,
} from "@material-ui/core";
import DeleteIcon from "@mui/icons-material/Delete";
import { useDropzone } from "react-dropzone";
import "./PhotoUploader.scss";
import ReusableSpinner from "../ReactTable/ReusableSpinner";
import { CREATE_OR_UPDATE_SYMPOSIUM_DATA } from "../Graphql/Symposium/symposiumMutation";
import { mutateData } from "../Service/graphQlService";
import { getNowDate } from "../../utils";

const PhotoUploader = ({
  showWebsite,
  setShowWebsite,
  eventData,
  eventTitle,
  eventDescription,
  eventImages,
  eventVideos,
  arcimedesRowId,
  setIsDialogOpen,
  isDialogOpen,
  setIsLoading,
  setErrorMsg,
  refetchPhotos,
  isLoading,
  isEditMode,
}) => {

  /* ===============================
     STATE
  =============================== */

  const [filesToUpload, setFilesToUpload] = useState([]);
  const [videoLinks, setVideoLinks] = useState([""]);
  const [addTitle, setAddTitle] = useState("");
  const [addDescription, setAddDescription] = useState("");
  const [titleError, setTitleError] = useState("");
  const [descriptionError, setDescriptionError] = useState("");

  /* ===============================
     PREFILL (ADD / EDIT)
  =============================== */

  useEffect(() => {

    if (!isDialogOpen) return;

    // ADD MODE RESET
    if (!isEditMode) {
      setAddTitle("");
      setAddDescription("");
      setVideoLinks([""]);
      setFilesToUpload([]);
      setTitleError("");
      setDescriptionError("");
      return;
    }

    // EDIT MODE PREFILL
    setAddTitle(eventTitle || "");
    setAddDescription(eventDescription || "");

    //  FIXED: Use originalUrl if exists
    if (eventVideos && eventVideos.length > 0) {
      setVideoLinks(
        eventVideos.map(function (video) {
          return video.originalUrl ? video.originalUrl : video.url;
        })
      );
    } else {
      setVideoLinks([""]);
    }

    if (eventImages && eventImages.length > 0) {
      const mapped = eventImages.map(function (img) {
        return {
          id: img.id,
          filename: img.url ? img.url.split("/").pop() : "",
          imageData: null,
          preview: img.url,
          existing: true,
          status: "Active"
        };
      });

      setFilesToUpload(mapped);
    } else {
      setFilesToUpload([]);
    }

  }, [
    isDialogOpen,
    isEditMode,
    eventTitle,
    eventDescription,
    eventImages,
    eventVideos
  ]);

  /* ===============================
     VIDEO HANDLERS
  =============================== */

  const handleVideoLinkChange = function (index, value) {
    const updated = [...videoLinks];
    updated[index] = value;
    setVideoLinks(updated);
  };

  const addVideoLinkField = function () {
    setVideoLinks([...videoLinks, ""]);
  };

  const removeVideoLinkField = function (index) {
    const updated = videoLinks.filter(function (_, i) {
      return i !== index;
    });
    setVideoLinks(updated.length ? updated : [""]);
  };

  /* ===============================
     DROPZONE
  =============================== */

  const onDrop = useCallback(function (acceptedFiles) {

    const imageFiles = acceptedFiles.filter(function (file) {
      return file.type.startsWith("image/");
    });

    const toBase64 = function (file) {
      return new Promise(function (resolve, reject) {
        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = function () {
          resolve(reader.result);
        };
        reader.onerror = reject;
      });
    };

    Promise.all(
      imageFiles.map(async function (file) {
        const base64 = await toBase64(file);
        return {
          id: file.name + "-" + Date.now(),
          filename: file.name,
          imageData: base64,
          preview: URL.createObjectURL(file),
          existing: false,
          status: "Active"
        };
      })
    ).then(function (files) {
      setFilesToUpload(function (prev) {
        return [...prev, ...files];
      });
    });

  }, []);

  const { getRootProps, getInputProps } = useDropzone({
    accept: { "image/*": [] },
    multiple: true,
    onDrop,
  });

  /* ===============================
     DELETE IMAGE
  =============================== */

  const removeFile = function (id) {

    setFilesToUpload(function (prev) {
      return prev
        .map(function (file) {

          if (file.id !== id) return file;

          if (file.existing) {
            return { ...file, status: "Inactive" };
          }

          return null;

        })
        .filter(Boolean);
    });
  };

  const handleCancel = function () {
    setIsDialogOpen(false);
    setAddTitle("");
    setAddDescription("");
    setVideoLinks([""]);
    setFilesToUpload([]);
    setTitleError("");
    setDescriptionError("");
    setShowWebsite("y");
  };

  /* ===============================
     SAVE
  =============================== */

  const handleSave = async function () {

    let isValid = true;

    if (!addTitle.trim()) {
      setTitleError("Please add title");
      isValid = false;
    } else {
      setTitleError("");
    }

    if (!addDescription.trim()) {
      setDescriptionError("Please add description");
      isValid = false;
    } else {
      setDescriptionError("");
    }

    if (!isValid) return;

    setIsLoading(true);

    const cleanedVideoLinks = videoLinks
      .filter(function (link) {
        return link.trim() !== "";
      })
      .map(function (link) {
        return { url: link.trim() };
      });

    const cleanedPhotos = filesToUpload
      .filter(function (item) {
        if (item.existing && item.status === "Inactive") return true;
        if (!item.existing) return true;
        return false;
      })
      .map(function (item) {

        if (item.existing && item.status === "Inactive") {
          return {
            id: item.id,
            status: "Inactive"
          };
        }

        return {
          filename: item.filename,
          folder: "app-images",
          imageData: item.imageData,
          status: "Active"
        };
      });

    const currentDate = getNowDate().split(" ")[0];

    const payload = {
      arcimedes_row_id: isEditMode ? arcimedesRowId : undefined,
      title: addTitle.trim(),
      description: addDescription.trim(),
      date: currentDate,
      videoLinks: cleanedVideoLinks,
      photos: cleanedPhotos,
      showWebsite: showWebsite,
    };

    try {

      await mutateData(CREATE_OR_UPDATE_SYMPOSIUM_DATA, { input: payload });

      setErrorMsg(null);
      await refetchPhotos();
      setIsDialogOpen(false);

    } catch (error) {

      setErrorMsg(
        error && error.message ? error.message : "An unexpected error occurred"
      );

    } finally {
      setIsLoading(false);
    }
  };

  /* ===============================
     RENDER
  =============================== */

  return (
    <div className="photo-uploader">
      <Dialog open={isDialogOpen} maxWidth="md" fullWidth>

        <DialogTitle style={{ padding: "16px 24px" }}>
          <div style={{ display: "flex", alignItems: "center", width: "100%" }}>

            <div style={{ fontSize: "18px", fontWeight: 500 }}>
              {isEditMode ? "Edit Event" : "Add Event"}
            </div>

            <div style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: "8px" }}>
              <span style={{ fontSize: "13px", color: "#666" }}>
                Website
              </span>

              <div
                onClick={function () {
                  setShowWebsite(showWebsite === "y" ? "n" : "y");
                }}
                style={{
                  width: "40px",
                  height: "20px",
                  borderRadius: "20px",
                  backgroundColor: showWebsite === "y" ? "#7EB233" : "#ccc",
                  position: "relative",
                  cursor: "pointer",
                  transition: "all 0.3s ease"
                }}
              >
                <div
                  style={{
                    width: "16px",
                    height: "16px",
                    backgroundColor: "#fff",
                    borderRadius: "50%",
                    position: "absolute",
                    top: "2px",
                    left: showWebsite === "y" ? "22px" : "2px",
                    transition: "all 0.3s ease",
                    boxShadow: "0 1px 3px rgba(0,0,0,0.3)"
                  }}
                />
              </div>
            </div>

          </div>
        </DialogTitle>

        <DialogContent>

          {isLoading && <ReusableSpinner />}

          <TextField
            label="Title *"
            fullWidth
            value={addTitle}
            onChange={(e) => setAddTitle(e.target.value)}
            variant="outlined"
            error={!!titleError}
            helperText={titleError}
            margin="normal"
            InputLabelProps={{ shrink: true }}
          />

          <TextField
            label="Description *"
            fullWidth
            multiline
            rows={4}
            value={addDescription}
            onChange={(e) => setAddDescription(e.target.value)}
            variant="outlined"
            error={!!descriptionError}
            helperText={descriptionError}
            style={{ marginBottom: "20px" }}
          />

          <div style={{ marginBottom: "20px" }}>
            <label>Video Links (Optional)</label>

            {videoLinks.map(function (link, index) {
              return (
                <div key={index} style={{ display: "flex", gap: "10px", marginTop: "10px" }}>
                  <TextField
                    fullWidth
                    placeholder="Paste YouTube URL"
                    value={link}
                    onChange={(e) =>
                      handleVideoLinkChange(index, e.target.value)
                    }
                  />

                  {videoLinks.length > 1 && (
                    <IconButton onClick={() => removeVideoLinkField(index)}>
                      <DeleteIcon style={{ color: "red" }} />
                    </IconButton>
                  )}
                </div>
              );
            })}

            <button
              type="button"
              className="button-new positive"
              style={{ marginTop: "10px" }}
              onClick={addVideoLinkField}
            >
              + Add Video
            </button>
          </div>

          <div
            {...getRootProps()}
            className="dropzone"
            style={{
              border: "2px dashed #7EB233",
              padding: "30px",
              textAlign: "center",
              marginBottom: "10px"
            }}
          >
            <input {...getInputProps()} />
            Drag & Drop images here
          </div>

          <div className="uploader-grid">
            {filesToUpload
              .filter(file => file.status !== "Inactive")
              .map(file => (
                <div key={file.id} className="uploader-card">
                  <img src={file.preview} alt="" className="uploader-img" />
                  <div
                    className="uploader-delete"
                    onClick={() => removeFile(file.id)}
                  >
                    ✕
                  </div>
                </div>
              ))}
          </div>

        </DialogContent>

        <DialogActions>
          <Button onClick={handleCancel}>Cancel</Button>

          <button
            className="button-new positive"
            onClick={handleSave}
            disabled={isLoading}
            style={{
              opacity: isLoading ? 0.6 : 1,
              cursor: isLoading ? "not-allowed" : "pointer"
            }}
          >
            Save
          </button>
        </DialogActions>

      </Dialog>
    </div>
  );
};

export default PhotoUploader;